<script>
import { mapGetters } from 'vuex';
import {
  getSortedAgentsByAvailability,
  getAgentsByUpdatedPresence,
} from 'dashboard/helper/agentHelper.js';
import MenuItem from './menuItem.vue';
import MenuItemWithSubmenu from './menuItemWithSubmenu.vue';
import wootConstants from 'dashboard/constants/globals';
import AgentLoadingPlaceholder from './agentLoadingPlaceholder.vue';
import { isAceleresIndividualOwn } from 'dashboard/constants/aceleresConnectionTypes';
import { isAceleresConversationStarted } from 'dashboard/helper/aceleresConversationState';
import ContactClassificationPicker from '../ContactClassificationPicker.vue';
import { resolveContactClassification } from 'dashboard/constants/contactClassifications';

const MENU = {
  MARK_AS_READ: 'mark-as-read',
  MARK_AS_UNREAD: 'mark-as-unread',
  PRIORITY: 'priority',
  PIN: 'pin',
  STATUS: 'status',
  SNOOZE: 'snooze',
  START: 'start',
  SELF_ASSIGN: 'self-assign',
  MINE: 'mine',
  AGENT: 'agent',
  PERSONAL: 'personal',
  TEAM: 'team',
  LABEL: 'label',
  DELETE: 'delete',
  OPEN_NEW_TAB: 'open-new-tab',
  COPY_LINK: 'copy-link',
  OWNERSHIP_TRANSFER: 'ownership-transfer',
  CLASSIFICATION: 'classification',
};

export default {
  components: {
    MenuItem,
    MenuItemWithSubmenu,
    AgentLoadingPlaceholder,
    ContactClassificationPicker,
  },
  props: {
    status: {
      type: String,
      default: '',
    },
    hasUnreadMessages: {
      type: Boolean,
      default: false,
    },
    inboxId: {
      type: Number,
      default: null,
    },
    teamId: {
      type: Number,
      default: null,
    },
    priority: {
      type: String,
      default: null,
    },
    isPinned: {
      type: Boolean,
      default: false,
    },
    conversationLabels: {
      type: Array,
      default: () => [],
    },
    activeAssigneeTab: {
      type: String,
      default: '',
    },
    assigneeId: {
      type: Number,
      default: null,
    },
    // Aceleres: titulos que esta funcionalidade criou — so neles o caixote
    // aparece. Chega pronto do cartao; o menu nao decide a regra.
    deletableTitles: {
      type: Array,
      default: () => [],
    },
    currentUserId: {
      type: Number,
      default: null,
    },
    additionalAttributes: {
      type: Object,
      default: () => ({}),
    },
    conversationStatus: {
      type: String,
      default: '',
    },
    inboxConnectionType: {
      type: String,
      default: '',
    },
    inboxConnectionStatus: {
      type: String,
      default: '',
    },
    inboxInstanceId: {
      type: [Number, String],
      default: null,
    },
    allowedOptions: {
      type: Array,
      default: () => [],
    },
    canRequestOwnership: {
      type: Boolean,
      default: false,
    },
    ownershipRequestPending: {
      type: Boolean,
      default: false,
    },
    contact: {
      type: Object,
      default: () => ({}),
    },
    classificationUpdating: {
      type: Boolean,
      default: false,
    },
    mobileSheet: {
      type: Boolean,
      default: false,
    },
    focusedMenuKey: {
      type: String,
      default: '',
    },
  },
  emits: [
    'updateConversation',
    'assignPriority',
    'togglePin',
    'markAsUnread',
    'markAsRead',
    'startConversation',
    'assignSelf',
    'assignAgent',
    'assignMine',
    'assignPersonal',
    'assignTeam',
    'assignLabel',
    'removeLabel',
    'createLabel',
    'deleteLabel',
    'requestOwnershipTransfer',
    'classifyContact',
  ],
  data() {
    return {
      MENU,
      STATUS_TYPE: wootConstants.STATUS_TYPE,
      activeMobileSubmenuKey: this.focusedMenuKey || null,
      // Aceleres: termo digitado no campo de pesquisa da seccao de etiquetas.
      // Zera sempre que a seccao abre ou fecha.
      labelSearch: '',
      readOption: {
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.MARK_AS_READ'),
        icon: 'mail',
      },
      unreadOption: {
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.MARK_AS_UNREAD'),
        icon: 'mail-unread',
      },
      statusMenuConfig: [
        {
          key: wootConstants.STATUS_TYPE.RESOLVED,
          label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.RESOLVED'),
          icon: 'checkmark',
        },
      ],
      priorityConfig: {
        key: MENU.PRIORITY,
        label: this.$t('CONVERSATION.PRIORITY.TITLE'),
        icon: 'warning',
        options: [
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
            key: null,
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
            key: 'urgent',
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
            key: 'high',
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
            key: 'medium',
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
            key: 'low',
          },
        ],
      },
      labelMenuConfig: {
        key: MENU.LABEL,
        icon: 'tag',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_LABEL'),
      },
      // Aceleres: criar etiqueta sem sair da lista de conversas. A cor vem da
      // etapa do funil, entao aqui tambem so o nome e pedido.
      createLabelConfig: {
        key: 'create-label',
        icon: 'i-lucide-plus',
        label: this.$t('CONTACT_PANEL.LABELS.LABEL_SELECT.CREATE_LABEL'),
      },
      agentMenuConfig: {
        key: MENU.AGENT,
        icon: 'person-add',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_AGENT'),
      },
      startOption: {
        key: MENU.START,
        icon: 'play',
        label: 'Iniciar atendimento',
      },
      selfAssignOption: {
        key: MENU.SELF_ASSIGN,
        icon: 'person-add',
        label: this.$t('CONVERSATION.ASSIGN_TO_ME'),
      },
      // Aceleres: as travessias entre as caixas são ações explícitas. A
      // desatribuição automática continua bloqueada no backend para evitar
      // alternância, mas o usuário pode devolver uma conversa de origem
      // pessoal quando essa for a decisão operacional.
      mineOption: {
        key: MENU.MINE,
        icon: 'person-add',
        label: 'Mover para meus',
      },
      personalOption: {
        key: MENU.PERSONAL,
        icon: 'user-minus',
        label: 'Mover para pessoais',
      },
      teamMenuConfig: {
        key: MENU.TEAM,
        icon: 'people-team-add',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_TEAM'),
      },
    };
  },
  computed: {
    ...mapGetters({
      labels: 'labels/getLabels',
      teams: 'teams/getTeams',
      assignableAgentsUiFlags: 'inboxAssignableAgents/getUIFlags',
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
    }),
    filteredAgentOnAvailability() {
      const agents = this.$store.getters[
        'inboxAssignableAgents/getAssignableAgents'
      ](this.inboxId);
      const agentsByUpdatedPresence = getAgentsByUpdatedPresence(
        agents,
        this.currentUser,
        this.currentAccountId
      );
      const filteredAgents = getSortedAgentsByAvailability(
        agentsByUpdatedPresence
      );
      return filteredAgents;
    },
    assignableAgents() {
      return [
        {
          confirmed: true,
          name: 'Ninguém',
          id: null,
          role: 'agent',
          account_id: 0,
          email: 'None',
        },
        ...this.filteredAgentOnAvailability,
      ];
    },
    isOpenConversation() {
      return (
        (this.conversationStatus || this.status) ===
        wootConstants.STATUS_TYPE.OPEN
      );
    },
    isStarted() {
      return isAceleresConversationStarted({
        additional_attributes: this.additionalAttributes,
      });
    },
    isPersonalConversation() {
      return (
        isAceleresIndividualOwn(this.inboxConnectionType) ||
        !!this.additionalAttributes?.personal_owner_chatwoot_user_id ||
        !!this.additionalAttributes?.personal_origin_chatwoot_user_id
      );
    },
    isAssignedToCurrentUser() {
      return Number(this.assigneeId) === Number(this.currentUserId);
    },
    /** Mesmo recorte da caixa de etiquetas da ficha: so gestor cria. */
    isAdmin() {
      return this.currentUser?.role === 'administrator';
    },
    // Aceleres 2026-09-02: a seccao de etiquetas segue a mesma ideia das
    // seccoes do menu de filtros — a lista longa filtra-se por um campo de
    // texto no topo. Sem termo, a lista e a de sempre.
    filteredLabels() {
      const term = this.labelSearch.trim().toLowerCase();
      if (!term) return this.labels;

      return this.labels.filter(label =>
        String(label.title || '')
          .toLowerCase()
          .includes(term)
      );
    },
    isPassiveSupervisorView() {
      if (!this.assigneeId || this.isAssignedToCurrentUser) return false;
      if (this.currentUser?.role === 'administrator') return true;

      return (this.teams || []).some(
        team =>
          Number(team.id) === Number(this.teamId) && Boolean(team.is_supervisor)
      );
    },
    canStartConversation() {
      return (
        this.isOpenConversation &&
        this.activeAssigneeTab === 'novos' &&
        !this.isPersonalConversation &&
        !this.isStarted &&
        (!this.assigneeId || this.isAssignedToCurrentUser)
      );
    },
    canSelfAssign() {
      if (
        this.inboxConnectionStatus !== 'connected' &&
        (this.inboxConnectionStatus || this.inboxInstanceId)
      ) {
        return false;
      }
      return (
        this.isOpenConversation &&
        !this.isPersonalConversation &&
        !this.isAssignedToCurrentUser
      );
    },
    // A saída de Pessoais e a entrada em Meus; a volta também precisa ser
    // explícita para não haver alternância causada por limpeza de assignee.
    canMoveToMine() {
      return (
        this.isOpenConversation &&
        this.activeAssigneeTab === wootConstants.ASSIGNEE_TYPE.PERSONAL &&
        this.isPersonalConversation
      );
    },
    canMoveToPersonal() {
      return (
        this.isOpenConversation &&
        this.activeAssigneeTab === wootConstants.ASSIGNEE_TYPE.ME &&
        this.isAssignedToCurrentUser &&
        this.isPersonalConversation
      );
    },
    ownershipTransferOption() {
      return {
        key: MENU.OWNERSHIP_TRANSFER,
        icon: this.ownershipRequestPending ? 'clock' : 'person-add',
        label: this.ownershipRequestPending
          ? 'Carteirização aguardando autorização'
          : 'Solicitar alteração de carteirização',
      };
    },
    pinOption() {
      return {
        key: MENU.PIN,
        icon: this.isPinned ? 'i-lucide-pin-off' : 'i-lucide-pin',
        label: this.isPinned ? 'Desafixar conversa' : 'Fixar conversa',
      };
    },
    contactClassification() {
      return resolveContactClassification(this.contact);
    },
    classificationMenuConfig() {
      return {
        key: MENU.CLASSIFICATION,
        icon: this.contactClassification.icon,
        label: `Classificação: ${this.contactClassification.label}`,
      };
    },
    startMenuOption() {
      if (!this.mobileSheet) return this.startOption;

      return { ...this.startOption, icon: 'i-lucide-play' };
    },
    mineMenuOption() {
      if (!this.mobileSheet) return this.mineOption;

      return { ...this.mineOption, icon: 'i-lucide-user-plus' };
    },
  },
  watch: {
    focusedMenuKey(value) {
      this.activeMobileSubmenuKey = value || null;
      this.labelSearch = '';
    },
  },
  mounted() {
    if (!this.focusedMenuKey) {
      this.$store.dispatch('inboxAssignableAgents/fetch', [this.inboxId]);
    }
  },
  methods: {
    classifyContact(classification) {
      this.$emit('classifyContact', classification);
    },
    // Aceleres 2026-09-02: o acordeao passou a valer tambem no desktop, entao
    // este metodo deixou de ser exclusivo da folha mobile. O nome do estado
    // (`activeMobileSubmenuKey`) ficou como estava para nao espalhar a
    // renomeacao pelas seis linhas que o leem; o que ele guarda e a seccao
    // aberta, em qualquer um dos dois desenhos. Uma de cada vez, como no
    // menu de filtros.
    toggleMobileSubmenu(key) {
      const vaiExpandir = this.activeMobileSubmenuKey !== key;
      this.activeMobileSubmenuKey = vaiExpandir ? key : null;
      // Abrir ou fechar uma seccao recomeca a pesquisa das etiquetas: o termo
      // de uma abertura anterior escondia metade da lista na seguinte.
      this.labelSearch = '';
      if (vaiExpandir) this.$nextTick(this.revelarSeccaoAberta);
    },
    // Aceleres 2026-09-02: expandir a ULTIMA seccao deixava as opcoes dela
    // fora da area visivel e obrigava a rolar a mao.
    //
    // E consequencia direta do acordeao ter chegado ao desktop no mesmo dia: o
    // submenu deixou de flutuar ao lado (onde nunca dependeu da altura do
    // menu) e passou a crescer POR DENTRO de um contentor com
    // `max-height: calc(100vh - 2rem)` e `overflow-y: auto`. Quem abre a
    // seccao de baixo abre-a abaixo do corte.
    //
    // A conta e feita com `getBoundingClientRect`, e nao com `offsetTop`: o
    // contentor nao e posicionado, entao o `offsetParent` das linhas e o
    // envelope do ContextMenu e o `offsetTop` viria contado a partir de outro
    // sitio. O `scrollTo` ja limita sozinho o valor ao maximo possivel.
    revelarSeccaoAberta() {
      const contentor = this.$el;
      // Sem barra de rolagem nao ha nada a revelar. E tambem o que mantem a
      // folha de rosto mobile de fora: la o teto de altura nao se aplica.
      if (!contentor?.scrollTo) return;
      if (contentor.scrollHeight <= contentor.clientHeight) return;

      const gatilho = contentor.querySelector('[aria-expanded="true"]');
      // A linha inteira, e nao so o botao: e ela que envolve o submenu
      // expandido, portanto e o `bottom` dela que interessa.
      const linha = gatilho?.closest('.menu-with-submenu');
      if (!linha) return;

      // O mesmo respiro do `padding` do menu, para a ultima linha nao ficar
      // colada ao rodape do contentor.
      const RESPIRO = 12;
      const excesso =
        linha.getBoundingClientRect().bottom +
        RESPIRO -
        contentor.getBoundingClientRect().bottom;
      if (excesso <= 0) return;

      contentor.scrollTo({
        top: contentor.scrollTop + excesso,
        behavior: 'smooth',
      });
    },
    isAllowed(keys) {
      if (!this.allowedOptions.length) return true;
      return keys.some(key => this.allowedOptions.includes(key));
    },
    toggleStatus(status, snoozedUntil) {
      this.$emit('updateConversation', status, snoozedUntil);
    },
    assignPriority(priority) {
      this.$emit('assignPriority', priority);
    },
    requestOwnershipTransfer() {
      if (this.ownershipRequestPending) return;

      this.$emit('requestOwnershipTransfer');
    },
    show(key) {
      // If the conversation status is same as the action, then don't display the option
      // i.e.: Don't show an option to resolve if the conversation is already resolved.
      return this.status !== key;
    },
    generateMenuLabelConfig(option, type = 'text') {
      return {
        key: option.id,
        ...(type === 'icon' && { icon: option.icon }),
        ...(type === 'label' && { color: option.color }),
        ...(type === 'agent' && { thumbnail: option.thumbnail }),
        ...(type === 'agent' && { status: option.availability_status }),
        ...(type === 'text' && { label: option.label }),
        ...(type === 'label' && { label: option.title }),
        ...(type === 'agent' && { label: option.name }),
        ...(type === 'team' && { label: option.name }),
      };
    },
  },
};
</script>

<template>
  <div
    class="acl-conversation-context-menu p-1 rounded-md shadow-xl bg-n-alpha-3/50 backdrop-blur-[100px] outline-1 outline outline-n-weak/50"
    :class="{
      'acl-conversation-context-menu--sheet': mobileSheet,
      'w-full touch-manipulation select-none divide-y divide-n-weak rounded-none bg-transparent px-3 py-1 shadow-none outline-none backdrop-blur-none [&_.menu]:!min-h-[52px] [&_.menu]:!w-full [&_.menu]:!rounded-none [&_.menu]:!bg-transparent [&_.menu]:!px-3 [&_.menu]:!text-n-slate-12 [&_.menu:hover]:!bg-transparent [&_.menu:active]:!bg-n-alpha-2 [&_.menu>span]:!size-5 [&_.menu>svg]:!size-5 [&_.menu-label]:!mx-3 [&_.menu-label]:!text-[15px]':
        mobileSheet,
    }"
  >
    <template
      v-if="
        !focusedMenuKey &&
        isAllowed([MENU.MARK_AS_READ, MENU.MARK_AS_UNREAD]) &&
        !isPassiveSupervisorView
      "
    >
      <MenuItem
        v-if="!hasUnreadMessages"
        :mobile="mobileSheet"
        :option="unreadOption"
        variant="icon"
        @click.stop="$emit('markAsUnread')"
      />
      <MenuItem
        v-else
        :mobile="mobileSheet"
        :option="readOption"
        variant="icon"
        @click.stop="$emit('markAsRead')"
      />
      <hr
        :hidden="mobileSheet"
        class="m-1 rounded border-b border-n-weak dark:border-n-weak"
      />
    </template>
    <template v-if="!focusedMenuKey && isAllowed([MENU.STATUS])">
      <template v-for="option in statusMenuConfig">
        <MenuItem
          v-if="show(option.key) && isAllowed([MENU.STATUS])"
          :key="option.key"
          :mobile="mobileSheet"
          :option="option"
          variant="icon"
          @click.stop="toggleStatus(option.key, null)"
        />
      </template>
      <hr
        :hidden="mobileSheet"
        class="m-1 rounded border-b border-n-weak dark:border-n-weak"
      />
    </template>
    <MenuItemWithSubmenu
      v-if="
        isAllowed([MENU.CLASSIFICATION]) &&
        (!focusedMenuKey || focusedMenuKey === MENU.CLASSIFICATION)
      "
      :option="classificationMenuConfig"
      :mobile="mobileSheet"
      :accordion="!mobileSheet"
      :expanded="activeMobileSubmenuKey === MENU.CLASSIFICATION"
      @toggle="toggleMobileSubmenu"
    >
      <ContactClassificationPicker
        :model-value="contactClassification.value"
        :disabled="classificationUpdating"
        :class="{
          '!w-full [&_button]:!min-h-[52px] [&_button]:!px-3 [&_button]:!text-[15px] [&_button:hover]:!bg-transparent [&_button:active]:!bg-n-alpha-2 [&_svg]:!size-5':
            mobileSheet,
        }"
        @select="classifyContact"
      />
    </MenuItemWithSubmenu>
    <hr
      v-if="!focusedMenuKey"
      :hidden="mobileSheet"
      class="m-1 rounded border-b border-n-weak dark:border-n-weak"
    />
    <MenuItem
      v-if="!focusedMenuKey && isAllowed([MENU.PIN])"
      :mobile="mobileSheet"
      :option="pinOption"
      variant="icon"
      @click.stop="$emit('togglePin')"
    />
    <hr
      v-if="!focusedMenuKey"
      :hidden="mobileSheet"
      class="m-1 rounded border-b border-n-weak dark:border-n-weak"
    />
    <template
      v-if="
        isAllowed([MENU.PRIORITY, MENU.LABEL, MENU.AGENT, MENU.TEAM]) &&
        (!focusedMenuKey ||
          focusedMenuKey === MENU.PRIORITY ||
          focusedMenuKey === MENU.LABEL)
      "
    >
      <MenuItemWithSubmenu
        v-if="
          isAllowed([MENU.PRIORITY]) &&
          (!focusedMenuKey || focusedMenuKey === MENU.PRIORITY)
        "
        :option="priorityConfig"
        :mobile="mobileSheet"
        :accordion="!mobileSheet"
        :expanded="activeMobileSubmenuKey === MENU.PRIORITY"
        @toggle="toggleMobileSubmenu"
      >
        <MenuItem
          v-for="(option, i) in priorityConfig.options"
          :key="i"
          :mobile="mobileSheet"
          :variant="option.key === priority ? 'label-assigned' : 'default'"
          :option="option"
          :priority-icon="option.key || ''"
          @click.stop="assignPriority(option.key)"
        />
      </MenuItemWithSubmenu>
      <MenuItemWithSubmenu
        v-if="
          isAllowed([MENU.LABEL]) &&
          (!focusedMenuKey || focusedMenuKey === MENU.LABEL)
        "
        :option="labelMenuConfig"
        :sub-menu-available="!!labels.length || isAdmin"
        :mobile="mobileSheet"
        :accordion="!mobileSheet"
        :expanded="activeMobileSubmenuKey === MENU.LABEL"
        @toggle="toggleMobileSubmenu"
      >
        <!-- Aceleres 2026-09-02: pesquisa e "criar etiqueta" vem ANTES da
             lista, como nas seccoes do menu de filtros. Antes era preciso
             rolar ate ao fim de dezenas de etiquetas para chegar ao "criar",
             e nao havia como chegar depressa a uma etiqueta pelo nome. -->
        <div class="acl-context-search-wrap">
          <div class="acl-context-search">
            <svg
              class="acl-context-search__icon"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <!-- `@keydown.stop`: o painel tem atalhos de teclado a ouvir na
                 janela; sem isto, escrever "e" no campo dispararia um deles. -->
            <input
              v-model="labelSearch"
              type="text"
              :placeholder="$t('CONTACT_PANEL.LABELS.LABEL_SELECT.PLACEHOLDER')"
              :aria-label="$t('CONTACT_PANEL.LABELS.LABEL_SELECT.PLACEHOLDER')"
              class="acl-context-search__input"
              @click.stop
              @keydown.stop
            />
          </div>
        </div>
        <MenuItem
          v-if="isAdmin"
          :mobile="mobileSheet"
          :option="createLabelConfig"
          variant="icon"
          @click.stop="$emit('createLabel')"
        />
        <MenuItem
          v-for="label in filteredLabels"
          :key="label.id"
          :mobile="mobileSheet"
          :option="generateMenuLabelConfig(label, 'label')"
          :variant="
            conversationLabels.includes(label.title)
              ? 'label-assigned'
              : 'label'
          "
          :deletable="isAdmin && deletableTitles.includes(label.title)"
          :delete-label-text="`${$t(
            'CONTACT_PANEL.LABELS.LABEL_SELECT.DELETE_ACTION'
          )} ${label.title}`"
          @click.stop="
            conversationLabels.includes(label.title)
              ? $emit('removeLabel', label)
              : $emit('assignLabel', label)
          "
          @delete="$emit('deleteLabel', label)"
        />
        <p v-if="!filteredLabels.length" class="acl-context-empty">
          {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.NO_RESULT') }}
        </p>
      </MenuItemWithSubmenu>
      <MenuItemWithSubmenu
        v-if="!focusedMenuKey && isAllowed([MENU.AGENT])"
        :option="agentMenuConfig"
        :sub-menu-available="!!assignableAgents.length"
        :mobile="mobileSheet"
        :accordion="!mobileSheet"
        :expanded="activeMobileSubmenuKey === MENU.AGENT"
        @toggle="toggleMobileSubmenu"
      >
        <AgentLoadingPlaceholder v-if="assignableAgentsUiFlags.isFetching" />
        <template v-else>
          <MenuItem
            v-for="agent in assignableAgents"
            :key="agent.id"
            :mobile="mobileSheet"
            :option="generateMenuLabelConfig(agent, 'agent')"
            variant="agent"
            @click.stop="$emit('assignAgent', agent)"
          />
        </template>
      </MenuItemWithSubmenu>
      <MenuItem
        v-if="!focusedMenuKey && canRequestOwnership"
        :mobile="mobileSheet"
        :option="ownershipTransferOption"
        variant="icon"
        :class="{ 'cursor-not-allowed opacity-60': ownershipRequestPending }"
        :aria-disabled="ownershipRequestPending"
        @click.stop="requestOwnershipTransfer"
      />
      <MenuItem
        v-if="!focusedMenuKey && canSelfAssign && isAllowed([MENU.AGENT])"
        :mobile="mobileSheet"
        :option="selfAssignOption"
        variant="icon"
        @click.stop="$emit('assignSelf')"
      />
      <MenuItem
        v-if="!focusedMenuKey && canStartConversation && isAllowed([MENU.AGENT])"
        :mobile="mobileSheet"
        :option="startMenuOption"
        variant="icon"
        @click.stop="$emit('startConversation')"
      />
      <MenuItem
        v-if="!focusedMenuKey && canMoveToMine && isAllowed([MENU.AGENT])"
        :mobile="mobileSheet"
        :option="mineMenuOption"
        variant="icon"
        @click.stop="$emit('assignMine')"
      />
      <MenuItem
        v-if="!focusedMenuKey && canMoveToPersonal && isAllowed([MENU.AGENT])"
        :mobile="mobileSheet"
        :option="personalOption"
        variant="icon"
        @click.stop="$emit('assignPersonal')"
      />
      <MenuItemWithSubmenu
        v-if="!focusedMenuKey && isAllowed([MENU.TEAM])"
        :option="teamMenuConfig"
        :sub-menu-available="!!teams.length"
        :mobile="mobileSheet"
        :accordion="!mobileSheet"
        :expanded="activeMobileSubmenuKey === MENU.TEAM"
        @toggle="toggleMobileSubmenu"
      >
        <MenuItem
          v-for="team in teams"
          :key="team.id"
          :mobile="mobileSheet"
          :option="generateMenuLabelConfig(team, 'team')"
          @click.stop="$emit('assignTeam', team)"
        />
      </MenuItemWithSubmenu>
    </template>
  </div>
</template>

<!-- Aceleres 2026-09-02: o menu do botao direito do cartao de conversa ganhou
     classe propria (`acl-conversation-context-menu`) e passou a vestir o mesmo
     visual do menu de ordenar (`#acl-pill-sort-popover`, em ChatTypeTabs.vue):
     cartoes brancos de 48px com borda #e1e4e8 e raio 16, texto 14px/400 em
     #60646c e icones de 20px na mesma cor. As medidas sao copia das regras
     `.acl-pill-sort-item*` — mexer num lado sem o outro desfaz o par.
     O estilo NAO e `scoped` de proposito: precisa alcancar os elementos dos
     componentes filhos (menuItem / menuItemWithSubmenu). Todo seletor comeca
     pela classe do menu, e a folha de rosto mobile (`--sheet`) fica de fora,
     porque ali o desenho e outro. -->
<style lang="scss">
.acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet) {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid #e1e4e8;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
  backdrop-filter: none;
  outline: none;
  box-sizing: border-box;
  /* O submenu deixou de flutuar ao lado e passou a expandir para baixo
     (2026-09-02): o menu cresce ao abrir uma seccao e, sem teto, passava do
     rodape da janela — o `ContextMenu.vue` so mede a altura no instante em
     que abre, nao depois. O que exceder rola por dentro; a barra e
     invisivel, como nos outros menus (regra no aceleres-overrides.css). */
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  overscroll-behavior: contain;
}

/* Os cartoes ficam separados pelo `gap`; as linhas divisorias que o menu
   antigo usava deixam de fazer sentido. */
.acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet) hr {
  display: none;
}

.acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet) .menu,
.acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .menu-with-submenu {
  width: 100%;
  min-width: 15rem;
  min-height: 48px;
  height: auto;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid #e1e4e8;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  color: #60646c;
  font-size: 14px;
  font-weight: 400;
  backdrop-filter: none;
  box-sizing: border-box;
  transition:
    background-color 120ms ease,
    border-color 120ms ease,
    box-shadow 120ms ease;
}

.acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .menu:hover,
.acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .menu-with-submenu:hover {
  background-color: #f8fafc;
  border-color: #d7dce2;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.1);
  color: #60646c;
}

.acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .menu-label,
.acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .menu-with-submenu
  p {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  color: inherit;
}

/* Icone: 18px e cinzento. As duas trilhas de icone do item (classe `i-*`
   desenhada por mascara e o <svg> do fluent-icon) levam a mesma medida. */
.acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .menu
  > span[class*='i-'],
.acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .menu
  > svg,
.acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .menu-with-submenu
  .menu-icon {
  width: 18px;
  height: 18px;
  color: #60646c;
  flex-shrink: 0;
}

.acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .menu-with-submenu
  > div
  > svg:last-child {
  color: #60646c;
}

/* O submenu e uma caixa do mesmo material que o menu.
   NAO declarar `display` aqui: quem manda no `display` do submenu sao as
   utilidades do `menuItemWithSubmenu.vue` (`hidden` + `group-hover/submenu:block`).
   Um `display: flex` neste seletor vence o `hidden` por especificidade
   (0,3,0 contra 0,1,0) e deixa TODOS os submenus abertos ao lado do menu.
   O espacamento entre as linhas vem do `+ *`, que dispensa `gap`/flex. */
.acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .submenu {
  padding: 12px;
  border: 1px solid #e1e4e8;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
  box-sizing: border-box;
  font-size: 14px;
}

.acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .submenu
  > *
  + * {
  margin-top: 8px;
}

.dark .acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet),
.dark
  .acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .submenu {
  border-color: #2c2f33;
  background: #1f2123;
}

.dark
  .acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .menu,
.dark
  .acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .menu-with-submenu {
  border-color: #34383d;
  background: #202326;
  color: #e5e5e5;
}

.dark
  .acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .menu:hover,
.dark
  .acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .menu-with-submenu:hover {
  border-color: #454a50;
  background-color: #292d31;
  color: #e5e5e5;
}

.dark
  .acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .menu
  > span[class*='i-'],
.dark
  .acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .menu
  > svg,
.dark
  .acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .menu-with-submenu
  .menu-icon {
  color: #a1a1aa;
}

/* Aceleres 2026-09-02: campo de pesquisa da seccao de etiquetas. O desenho e
   copia do `.acl-filter-search` do menu de filtros (AceleresDropdownFilter.vue):
   pill sem borda, fundo cinza translucido, anel cinza no hover e verde no foco.
   Mexer num lado sem o outro desfaz o par. Ao contrario do resto do menu, esta
   regra vale TAMBEM na folha mobile — o campo e o mesmo nos dois desenhos. */
.acl-conversation-context-menu .acl-context-search {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 12px;
  background: rgba(194, 189, 184, 0.15);
  border: none;
  border-radius: 50px;
  box-shadow: inset 0 0 0 0 transparent;
  transition: box-shadow 0.12s;
}

.acl-conversation-context-menu .acl-context-search:hover {
  box-shadow: inset 0 0 0 1px #c4c4c4;
}

.acl-conversation-context-menu .acl-context-search:focus-within {
  box-shadow: inset 0 0 0 1.5px #1b8755;
}

.acl-conversation-context-menu .acl-context-search__icon {
  flex-shrink: 0;
  color: #8a8a8a;
}

.acl-conversation-context-menu .acl-context-search__input {
  flex: 1;
  min-width: 0;
  height: 34px;
  padding: 0;
  margin: 0;
  background: transparent !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  font-size: 13px;
  line-height: 34px;
  color: #1f2937;
}

.acl-conversation-context-menu .acl-context-search__input::placeholder {
  color: #8a8a8a;
}

.acl-conversation-context-menu .acl-context-search__input:focus,
.acl-conversation-context-menu .acl-context-search__input:focus-visible {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
  background: transparent !important;
}

.acl-conversation-context-menu .acl-context-empty {
  margin: 0;
  padding: 8px 4px;
  font-size: 13px;
  color: #8a8a8a;
  text-align: center;
}

/* A lista rola por dentro do submenu (`max-h-[15rem]`), entao o campo fica
   colado ao topo. As margens negativas cobrem os 12px de padding do submenu —
   sem elas o conteudo rolado aparecia por cima e por baixo do campo. */
.acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .acl-context-search-wrap {
  position: sticky;
  top: -12px;
  z-index: 1;
  margin: -12px -12px 0;
  padding: 12px 12px 8px;
  background: #ffffff;
}

/* Na folha mobile o submenu tem so 4px de respiro e as linhas tem 60px de
   altura: o campo precisa de margem propria e de area de toque a condizer. */
.acl-conversation-context-menu--sheet .acl-context-search-wrap {
  padding: 8px 12px;
}

.acl-conversation-context-menu--sheet .acl-context-search__input {
  height: 44px;
  line-height: 44px;
  font-size: 16px; /* abaixo de 16px o iOS da zoom ao focar o campo */
}

.acl-conversation-context-menu .acl-context-search__input:-webkit-autofill {
  -webkit-box-shadow: 0 0 0 1000px transparent inset;
}

.dark .acl-conversation-context-menu .acl-context-search {
  background: rgba(255, 255, 255, 0.04);
}

.dark .acl-conversation-context-menu .acl-context-search:hover {
  box-shadow: inset 0 0 0 1px #3f3f46;
}

.dark .acl-conversation-context-menu .acl-context-search__icon,
.dark .acl-conversation-context-menu .acl-context-search__input::placeholder,
.dark .acl-conversation-context-menu .acl-context-empty {
  color: #a1a1aa;
}

.dark .acl-conversation-context-menu .acl-context-search__input {
  color: #e5e5e5;
}

.dark
  .acl-conversation-context-menu:not(.acl-conversation-context-menu--sheet)
  .acl-context-search-wrap {
  background: #1f2123;
}
</style>
